import React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'

import BedbankSelectDates from './BedbankSelectDates'
import BedbankSummaryPriceDetails from './BedbankSummaryPriceDetails'
import InlineGroup from 'components/Common/InlineGroup'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { pluralizeToString } from 'lib/pluralize'
import { DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import BedbankCancellationPolicy from './BedbankCancellationPolicy'
import WalledContent from 'components/Common/WalledContent'
import BedbankPromotionCancellationPolicy from 'components/OfferPage/Bedbank/BedbankPromotionCancellationPolicy'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import BedbankOfferInclusions from './BedbankOfferInclusions'

const ButtonSection = styled.div`
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;

  ${mediaQueryUp.lg} {
    min-width: ${rem(340)};
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const MainSection = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;

  ${mediaQueryUp.lg} {
    min-width: ${rem(340)};
  }

  > * + * {
    margin-top: ${rem(8)};
  }
`

const RateInclusionsSection = styled.div`
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
`

const TravelPeriodSection = styled.div`
  display: flex;
  flex-direction: column;
`

const WrapperFlex = styled(InlineGroup)`
  > * + * {
    margin-top: ${rem(16)};
  }

  flex-direction: column;

  ${mediaQueryUp.md} {
    flex-direction: row;

    > * + * {
      margin-top: 0;
      margin-left: ${rem(16)};
    }
  }
`

const WrapperColumn = styled(InlineGroup)`
  > * + * {
    margin-top: ${rem(6)};
  }

  flex-direction: column;
`

interface Props {
  promotion: App.BedbankPromotion;
  rooms: Array<App.Occupants>;
  offer: App.BedbankOffer;
  duration: number;
  showNonCancellation?: boolean;
  onlySalePeriod?: boolean;
  rate?: App.BedbankRate | App.OrderBedbankRate;
  direction?: 'column' | 'row';
  checkInDate?: moment.Moment;
  checkOutDate?: moment.Moment;
}

function BedbankPromotion(props: Props) {
  const validCheckinCheckoutDates = !!(props.checkInDate && props.checkOutDate)

  const { promotion, direction, rooms, offer, rate, onlySalePeriod, showNonCancellation, duration } = props

  const Wrapper = direction === 'column' ? WrapperColumn : WrapperFlex

  return (
    <Wrapper horizontal="start">
      <MainSection>
        <div>
          <ProductTypeLabel productType="limited_time_special"/>
        </div>
        <BodyText variant="medium" weight="bold">{promotion.name}</BodyText>
        {rate && (
          <BedbankCancellationPolicy rate={rate} offer={offer} showNonCancellation={showNonCancellation} />
        )}
        {!validCheckinCheckoutDates && promotion.cancellationPolicy && (
          <BedbankPromotionCancellationPolicy policyCode={promotion.cancellationPolicy} />
        )}
        <TravelPeriodSection>
          {!onlySalePeriod && <BodyText variant="medium" weight="normal" colour="neutral-two">Travel between: {moment(promotion.travelPeriod.from).format(DMY_CASUAL_SHORT_FORMAT)} - {moment(promotion.travelPeriod.to).format(DMY_CASUAL_SHORT_FORMAT)}</BodyText>}
          {!onlySalePeriod && <BodyText variant="medium" weight="normal" colour="neutral-two">Min stay: {pluralizeToString('night', promotion.los)}</BodyText>}
        </TravelPeriodSection>
      </MainSection>
      {!rate && (<>
        <RateInclusionsSection>
          <BedbankOfferInclusions
            content={promotion.rateInclusionsLong}
            inclusions={promotion.inclusions}
            maxHeight={180}
          />
        </RateInclusionsSection>
        <ButtonSection>
          {offer.sell && (
            <WalledContent enforced={offer.walledGarden}>
              <BedbankSummaryPriceDetails
                rooms={rooms}
                offer={offer}
                duration={duration}
                flightsEnabled={false}
              />
            </WalledContent>
          )}
          <BedbankSelectDates />
        </ButtonSection>
      </>)}
    </Wrapper>
  )
}

export default BedbankPromotion
